import { cn, formatYAxisTick } from "@/lib/utils";
import { totalDailyExpensesAtom, locale } from "@/store/global";
import { useViewportSize } from "@mantine/hooks";
import { format } from "date-fns";
import { useAtom } from "jotai";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

export function ExpenseBarChart() {
  const [dailyExpenses] = useAtom(totalDailyExpensesAtom);

  const data = Object.keys(dailyExpenses).map((date) => {
    return {
      date: date,
      name: dailyExpenses[date].dayOfWeek,
      amount: dailyExpenses[date].amount,
    };
  });

  const { width, height } = useViewportSize();
  const barSize = width < 435 ? 30 : 38;

  const isMobile = width < 435 || height <= 680;
  const isHighDevice = height > 895;

  function getResponsiveAspect() {
    if (width < 400) return undefined;
    if (height > 1000) return 0.9;
    if (height > 1000) return 1;
    if (height > 895) return 1.2;
    if (height > 600) return 1.3;
    return 1.4;
  }

  return (
    <div className={cn("-mt-32 pr-5", [isHighDevice && "!h-[35vh]", isMobile && "h-[40vh]"])}>
      <ResponsiveContainer aspect={getResponsiveAspect()} minWidth={"100%"} height="100%" width="100%">
        <BarChart margin={{ left: -14, bottom: 0 }} data={data} barSize={barSize}>
          <defs>
            <linearGradient id="barBackground" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#022c22" stopOpacity={0.1} />
              <stop offset="100%" stopColor="#ecfdf5" stopOpacity={0} />
            </linearGradient>

            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#064e3b" stopOpacity={1} />
              <stop offset="100%" stopColor="#022c22" stopOpacity={1} />
            </linearGradient>
          </defs>

          <YAxis
            tickFormatter={formatYAxisTick}
            width={64}
            fontSize={12}
            textAnchor="end"
            domain={([, dataMax]) => {
              if (dataMax) {
                return [0, dataMax + 20_000];
              }

              return [0, 100_000];
            }}
            tickLine={{ stroke: "#ccc", strokeOpacity: 0 }}
            padding={{ top: isMobile ? 120 : 110, bottom: 0 }}
            axisLine={{ stroke: "#ccc", strokeOpacity: 0 }}
          />

          <XAxis
            axisLine={{ stroke: "#ccc", strokeOpacity: 0 }}
            fontSize={12}
            tickLine={false}
            dataKey="name"
            padding={{ left: 10, right: 0 }}
          />

          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="rounded-lg bg-background p-2 pr-4 shadow-sm">
                    <div className="flex flex-col">
                      <span className="block text-xs uppercase text-muted-foreground">
                        {format(new Date(payload[0].payload.date), "dd MMM yyyy", { locale: locale })}
                      </span>
                      <span className="text-sm font-bold text-muted-foreground">
                        {new Intl.NumberFormat("id-ID", {
                          style: "currency",
                          currency: "IDR",
                          maximumFractionDigits: 0,
                        }).format(Number(payload[0].value))}
                      </span>
                    </div>
                  </div>
                );
              }

              return null;
            }}
          />
          <Bar background={{ fill: "url(#barBackground)", width: "40%" }} radius={[8, 8, 8, 8]} dataKey="amount" fill="#065f46" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
